/**
 * /one-on-ones — 1:1 Meeting Notes (Section 3B).
 *
 * Two-column layout:
 *   - Left column: sessions list (grouped by employee for managers,
 *     or chronological for reports), plus a "Schedule 1:1" button.
 *   - Right column: detail editor for the selected row — talking
 *     points, shared notes, private manager notes (only rendered
 *     when the caller is the manager), action items with checkboxes,
 *     mood emoji row, follow-up date.
 *
 * Access gating happens server-side; on the client we just render
 * what the API returned and hide the "private" block for non-manager
 * viewers (the backend redacts it anyway).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  CalendarPlus,
  CheckCircle2,
  CheckSquare,
  ChevronRight,
  Lock,
  MessageSquare,
  Plus,
  Smile,
  Square,
  Trash2,
} from "lucide-react";

import { pageTransition, formatDate } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";

import {
  completeOneOnOne,
  createOneOnOne,
  getOneOnOne,
  listOneOnOnes,
  updateOneOnOne,
  type ActionItem,
  type OneOnOne,
  type OneOnOneMood,
  type TalkingPoint,
} from "@/api/performance";
import { listEmployees } from "@/api/employees";
import type { EmployeeListItem } from "@/types/employee";

import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";

const MOOD_OPTIONS: { value: OneOnOneMood; emoji: string; label: string }[] = [
  { value: "great", emoji: "🤩", label: "Great" },
  { value: "good", emoji: "🙂", label: "Good" },
  { value: "okay", emoji: "😐", label: "Okay" },
  { value: "struggling", emoji: "😟", label: "Struggling" },
];

export function OneOnOnesPage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [sessions, setSessions] = useState<OneOnOne[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<OneOnOne | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);

  const isManagerRole =
    user?.role === "MANAGER" ||
    user?.role === "HR_ADMIN" ||
    user?.role === "SUPER_ADMIN";

  // Load sessions + directory (directory is needed for scheduling).
  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      // Participant default — server returns sessions where caller is
      // manager or report. Merge both for HR admins who have no direct
      // personal record via manager=me and report=me fallthrough.
      const [asMgr, asRep] = await Promise.all([
        listOneOnOnes({ manager: "me" }).catch(() => []),
        listOneOnOnes({ report: "me" }).catch(() => []),
      ]);
      const merged = new Map<string, OneOnOne>();
      [...asMgr, ...asRep].forEach((r) => merged.set(r.id, r));
      setSessions(
        [...merged.values()].sort((a, b) =>
          b.scheduled_at.localeCompare(a.scheduled_at),
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    // Only HR / Manager need the directory to pick a report when scheduling.
    if (!isManagerRole) return;
    // Backend caps per_page at 100 — anything larger 422s and the picker
    // silently stays empty.
    listEmployees(1, 100)
      .then((r) => setEmployees(r.items))
      .catch((err) => {
        console.error("OneOnOnesPage: listEmployees failed", err);
        setEmployees([]);
      });
  }, [isManagerRole]);

  // Fetch the full selected row (list endpoint may include redacted rows
  // already, but we re-fetch for the canonical copy when focusing a row).
  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    getOneOnOne(selectedId)
      .then((r) => {
        if (!cancelled) setSelected(r);
      })
      .catch(() => {
        if (!cancelled) addToast("error", "Couldn't load 1:1");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, addToast]);

  // ── Grouping for the left column ────────────────────────────────
  const groups = useMemo(() => {
    // Group by "the other participant" relative to the caller.
    const g = new Map<string, OneOnOne[]>();
    for (const s of sessions) {
      const otherId =
        s.manager_employee_id === selected?.manager_employee_id
          ? s.report_employee_id
          : s.manager_employee_id;
      const key = otherId;
      if (!g.has(key)) g.set(key, []);
      g.get(key)!.push(s);
    }
    return g;
  }, [sessions, selected?.manager_employee_id]);

  const isCallerManagerOfSelected = useMemo(() => {
    // Proxy: if the caller can see private_manager_notes (non-null, or any
    // session the caller has manager-authored), treat as manager.
    if (!selected) return false;
    // We don't have employee_id on the user object directly, so rely on
    // the redaction contract: if private_manager_notes is a string (even
    // ""), the server considered us manager/HR. If it's strictly null it
    // *may* be redacted — fall back to "role is admin".
    if (selected.private_manager_notes !== null) return true;
    return user?.role === "HR_ADMIN" || user?.role === "SUPER_ADMIN";
  }, [selected, user?.role]);

  // ── Actions ─────────────────────────────────────────────────────
  const patchSelected = useCallback(
    async (payload: Parameters<typeof updateOneOnOne>[1]) => {
      if (!selected) return;
      try {
        const next = await updateOneOnOne(selected.id, payload);
        setSelected(next);
        setSessions((prev) =>
          prev.map((s) => (s.id === next.id ? next : s)),
        );
      } catch {
        addToast("error", "Couldn't save changes");
      }
    },
    [selected, addToast],
  );

  const markComplete = useCallback(async () => {
    if (!selected) return;
    try {
      const next = await completeOneOnOne(selected.id);
      setSelected(next);
      setSessions((prev) =>
        prev.map((s) => (s.id === next.id ? next : s)),
      );
      addToast("success", "1:1 marked complete");
    } catch {
      addToast("error", "Couldn't complete 1:1");
    }
  }, [selected, addToast]);

  // ── Render ──────────────────────────────────────────────────────
  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="1:1 Meeting Notes"
        subtitle={
          loading
            ? "Loading…"
            : `${sessions.length} session${sessions.length === 1 ? "" : "s"}`
        }
        actions={
          isManagerRole ? (
            <Button onClick={() => setScheduleOpen(true)}>
              <CalendarPlus className="h-4 w-4 mr-1" /> Schedule 1:1
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: list */}
        <div className="lg:col-span-1 space-y-3">
          {loading ? (
            <p className="text-sm text-text-muted">Loading…</p>
          ) : sessions.length === 0 ? (
            <Card className="text-center py-12">
              <MessageSquare className="h-8 w-8 text-text-muted mx-auto mb-2" />
              <p className="text-text-primary font-medium">No 1:1s yet</p>
              <p className="text-sm text-text-muted mt-1">
                {isManagerRole
                  ? "Schedule your first 1:1 with a direct report."
                  : "Your manager hasn't scheduled one yet."}
              </p>
            </Card>
          ) : (
            [...groups.entries()].map(([otherId, items]) => (
              <Card key={otherId} padding={false} className="overflow-hidden">
                <div className="px-4 py-2 bg-background text-xs font-medium text-text-muted border-b border-border">
                  {items[0]
                    ? items[0].manager_employee_id === otherId
                      ? "With your manager"
                      : `With report · ${otherId.slice(0, 8)}`
                    : otherId}
                </div>
                <ul>
                  {items.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(s.id)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-background transition-colors ${
                          selectedId === s.id ? "bg-background" : ""
                        }`}
                      >
                        <div>
                          <p className="text-sm font-medium text-text-primary">
                            {formatDate(
                              s.scheduled_at,
                              "MMM d, yyyy · HH:mm",
                            )}
                          </p>
                          <p className="text-xs text-text-muted">
                            {s.action_items.length > 0 &&
                              `${s.action_items.filter((a) => !a.completed).length} open action · `}
                            {s.talking_points.length} talking point
                            {s.talking_points.length === 1 ? "" : "s"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              s.status === "completed"
                                ? "success"
                                : s.status === "cancelled"
                                  ? "default"
                                  : "info"
                            }
                          >
                            {s.status}
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-text-muted" />
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            ))
          )}
        </div>

        {/* Right: detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <OneOnOneDetail
              session={selected}
              isManagerView={isCallerManagerOfSelected}
              onPatch={patchSelected}
              onComplete={markComplete}
            />
          ) : (
            <Card className="text-center py-24">
              <MessageSquare className="h-10 w-10 text-text-muted mx-auto mb-2" />
              <p className="text-text-primary font-medium">
                Select a 1:1 to view details
              </p>
              <p className="text-sm text-text-muted mt-1">
                Your talking points, shared notes, and action items appear
                here.
              </p>
            </Card>
          )}
        </div>
      </div>

      {scheduleOpen && (
        <ScheduleModal
          employees={employees}
          onClose={() => setScheduleOpen(false)}
          onCreated={(s) => {
            setSessions((prev) => [s, ...prev]);
            setSelectedId(s.id);
            setScheduleOpen(false);
          }}
        />
      )}
    </motion.div>
  );
}

// ─────────────────────── Detail editor ───────────────────────

function OneOnOneDetail({
  session,
  isManagerView,
  onPatch,
  onComplete,
}: {
  session: OneOnOne;
  isManagerView: boolean;
  onPatch: (p: Parameters<typeof updateOneOnOne>[1]) => Promise<void>;
  onComplete: () => Promise<void>;
}) {
  const [sharedNotes, setSharedNotes] = useState(session.shared_notes ?? "");
  const [privateNotes, setPrivateNotes] = useState(
    session.private_manager_notes ?? "",
  );
  const [newTP, setNewTP] = useState("");
  const [newAI, setNewAI] = useState("");
  const [newAIDue, setNewAIDue] = useState("");
  const [followUp, setFollowUp] = useState(session.follow_up_date ?? "");

  useEffect(() => {
    setSharedNotes(session.shared_notes ?? "");
    setPrivateNotes(session.private_manager_notes ?? "");
    setFollowUp(session.follow_up_date ?? "");
  }, [session.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addTalkingPoint = async () => {
    const text = newTP.trim();
    if (!text) return;
    const next: TalkingPoint[] = [
      ...session.talking_points,
      { text, added_by_employee_id: null },
    ];
    await onPatch({ talking_points: next });
    setNewTP("");
  };

  const addActionItem = async () => {
    const text = newAI.trim();
    if (!text) return;
    const next: ActionItem[] = [
      ...session.action_items,
      {
        text,
        completed: false,
        due_date: newAIDue || null,
      },
    ];
    await onPatch({ action_items: next });
    setNewAI("");
    setNewAIDue("");
  };

  const toggleActionItem = async (i: number) => {
    const next = session.action_items.map((a, idx) =>
      idx === i ? { ...a, completed: !a.completed } : a,
    );
    await onPatch({ action_items: next });
  };

  const deleteActionItem = async (i: number) => {
    const next = session.action_items.filter((_, idx) => idx !== i);
    await onPatch({ action_items: next });
  };

  const deleteTalkingPoint = async (i: number) => {
    const next = session.talking_points.filter((_, idx) => idx !== i);
    await onPatch({ talking_points: next });
  };

  const setMood = async (mood: OneOnOneMood) => {
    await onPatch({ mood });
  };

  const saveSharedNotes = async () => {
    if (sharedNotes === (session.shared_notes ?? "")) return;
    await onPatch({ shared_notes: sharedNotes });
  };

  const savePrivateNotes = async () => {
    if (privateNotes === (session.private_manager_notes ?? "")) return;
    await onPatch({ private_manager_notes: privateNotes });
  };

  const saveFollowUp = async () => {
    if (followUp === (session.follow_up_date ?? "")) return;
    await onPatch({ follow_up_date: followUp || null });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              {formatDate(session.scheduled_at, "EEEE, MMM d, yyyy · HH:mm")}
            </h3>
            <p className="text-xs text-text-muted mt-1">
              Status: <Badge variant="info">{session.status}</Badge>
              {session.completed_at && (
                <span className="ml-2">
                  Completed{" "}
                  {formatDate(session.completed_at, "MMM d, yyyy")}
                </span>
              )}
            </p>
          </div>
          {session.status !== "completed" && isManagerView && (
            <Button variant="primary" onClick={onComplete}>
              <CheckCircle2 className="h-4 w-4 mr-1" /> Complete 1:1
            </Button>
          )}
        </div>
      </Card>

      {/* Talking points */}
      <Card>
        <h4 className="text-sm font-semibold text-text-primary mb-3">
          Talking points
        </h4>
        <ul className="space-y-1 mb-3">
          {session.talking_points.length === 0 && (
            <li className="text-sm text-text-muted italic">
              No talking points yet.
            </li>
          )}
          {session.talking_points.map((tp, i) => (
            <li
              key={i}
              className="flex items-center gap-2 text-sm text-text-primary py-1"
            >
              <span className="flex-1">• {tp.text}</span>
              <button
                type="button"
                onClick={() => deleteTalkingPoint(i)}
                className="text-text-muted hover:text-danger"
                aria-label="Remove talking point"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Input
            value={newTP}
            onChange={(e) => setNewTP(e.target.value)}
            placeholder="Add a topic to discuss…"
            onKeyDown={(e) => {
              if (e.key === "Enter") addTalkingPoint();
            }}
          />
          <Button variant="secondary" onClick={addTalkingPoint}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* Shared notes */}
      <Card>
        <h4 className="text-sm font-semibold text-text-primary mb-2">
          Shared notes{" "}
          <span className="text-xs font-normal text-text-muted">
            — visible to both
          </span>
        </h4>
        <textarea
          value={sharedNotes}
          onChange={(e) => setSharedNotes(e.target.value)}
          onBlur={saveSharedNotes}
          rows={5}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
          placeholder="What did you talk about? Highlights, decisions, follow-ups…"
        />
      </Card>

      {/* Private manager notes — rendered only when manager-view */}
      {isManagerView && (
        <Card className="border-l-4 border-l-warning">
          <h4 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
            <Lock className="h-4 w-4" /> Private notes
            <span className="text-xs font-normal text-text-muted">
              — only you and HR
            </span>
          </h4>
          <textarea
            value={privateNotes}
            onChange={(e) => setPrivateNotes(e.target.value)}
            onBlur={savePrivateNotes}
            rows={4}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
            placeholder="Performance observations, concerns, coaching notes…"
          />
        </Card>
      )}

      {/* Action items */}
      <Card>
        <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
          <CheckSquare className="h-4 w-4" /> Action items
        </h4>
        <ul className="space-y-2 mb-3">
          {session.action_items.length === 0 && (
            <li className="text-sm text-text-muted italic">
              No action items yet.
            </li>
          )}
          {session.action_items.map((ai, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <button
                type="button"
                onClick={() => toggleActionItem(i)}
                aria-label={ai.completed ? "Mark incomplete" : "Mark complete"}
                className="text-text-muted hover:text-accent"
              >
                {ai.completed ? (
                  <CheckSquare className="h-4 w-4 text-success" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
              </button>
              <span
                className={`flex-1 ${
                  ai.completed
                    ? "line-through text-text-muted"
                    : "text-text-primary"
                }`}
              >
                {ai.text}
              </span>
              {ai.due_date && (
                <span className="text-xs text-text-muted">
                  due {formatDate(ai.due_date, "MMM d")}
                </span>
              )}
              <button
                type="button"
                onClick={() => deleteActionItem(i)}
                className="text-text-muted hover:text-danger"
                aria-label="Remove action item"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Input
            value={newAI}
            onChange={(e) => setNewAI(e.target.value)}
            placeholder="New action item…"
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === "Enter") addActionItem();
            }}
          />
          <input
            type="date"
            value={newAIDue}
            onChange={(e) => setNewAIDue(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary"
          />
          <Button variant="secondary" onClick={addActionItem}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* Mood check-in */}
      <Card>
        <h4 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
          <Smile className="h-4 w-4" /> How are you feeling?
          <span className="text-xs font-normal text-text-muted">
            — shared with your manager
          </span>
        </h4>
        <div className="flex gap-2">
          {MOOD_OPTIONS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMood(m.value)}
              className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                session.mood === m.value
                  ? "border-accent bg-accent/10 text-text-primary"
                  : "border-border hover:bg-background text-text-muted"
              }`}
            >
              <span className="text-lg mr-1">{m.emoji}</span> {m.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Follow-up */}
      <Card>
        <h4 className="text-sm font-semibold text-text-primary mb-2">
          Next 1:1
        </h4>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            onBlur={saveFollowUp}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary"
          />
          <p className="text-xs text-text-muted">
            Seeds the scheduled date for the next session.
          </p>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────── Schedule modal ───────────────────────

function ScheduleModal({
  employees,
  onClose,
  onCreated,
}: {
  employees: EmployeeListItem[];
  onClose: () => void;
  onCreated: (s: OneOnOne) => void;
}) {
  const { user } = useAuth();
  const { addToast } = useToast();

  // Admins may schedule on behalf of any manager → report pair; regular
  // managers always schedule as themselves (the backend rejects anything
  // else with 403). We only surface the manager picker for admin roles.
  const isAdmin =
    user?.role === "HR_ADMIN" || user?.role === "SUPER_ADMIN";
  const myEmployeeId =
    (user as unknown as { employee_id?: string | null } | null)?.employee_id ??
    null;

  const [managerId, setManagerId] = useState<string>(myEmployeeId ?? "");
  const [reportId, setReportId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [agenda, setAgenda] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reportId || !scheduledAt) {
      addToast("error", "Please pick an employee and time");
      return;
    }
    // Admins must choose a manager explicitly when they don't have their
    // own employee record (e.g. the super admin who created the org via
    // /signup/org and isn't in the employee roster). Non-admins always
    // use their own employee id.
    const effectiveManagerId = isAdmin ? managerId : myEmployeeId;
    if (!effectiveManagerId) {
      addToast(
        "error",
        isAdmin
          ? "Please choose the manager running this 1:1"
          : "No employee record linked to your account — ask an HR admin to link your profile",
      );
      return;
    }
    if (effectiveManagerId === reportId) {
      addToast("error", "Manager and report must be different people");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createOneOnOne({
        manager_employee_id: effectiveManagerId,
        report_employee_id: reportId,
        scheduled_at: new Date(scheduledAt).toISOString(),
        agenda: agenda.trim() || null,
      });
      onCreated(created);
    } catch (err: unknown) {
      // Surface the backend detail instead of a generic toast so the user
      // sees "manager and report must be different employees", "employee
      // not found", 403s, etc. Axios shape: err.response.data.detail.
      const ax = err as {
        response?: { status?: number; data?: { detail?: unknown } };
        message?: string;
        code?: string;
      };
      const raw = ax?.response?.data?.detail;
      let message = "Couldn't schedule 1:1";
      if (typeof raw === "string" && raw.trim()) {
        message = raw;
      } else if (raw && typeof raw === "object") {
        message = JSON.stringify(raw);
      } else if (ax?.response?.status) {
        message = `Couldn't schedule 1:1 (HTTP ${ax.response.status})`;
      } else if (ax?.code === "ERR_NETWORK") {
        message = "Network error — check your connection and try again";
      } else if (ax?.message) {
        message = ax.message;
      }
      addToast("error", message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Schedule a 1:1">
      <div className="space-y-4">
        {isAdmin && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Manager
            </label>
            <select
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary"
            >
              <option value="">Select the manager…</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.first_name} {e.last_name} — {e.title}
                </option>
              ))}
            </select>
            {!myEmployeeId && (
              <p className="mt-1 text-xs text-text-muted">
                Your admin account isn&apos;t linked to an employee record,
                so pick the manager this 1:1 belongs to.
              </p>
            )}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Direct report
          </label>
          <select
            value={reportId}
            onChange={(e) => setReportId(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary"
          >
            <option value="">Select an employee…</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.first_name} {e.last_name} — {e.title}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Date &amp; time
          </label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Agenda (optional)
          </label>
          <textarea
            value={agenda}
            onChange={(e) => setAgenda(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary"
            placeholder="What should you cover?"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={submitting}>
          {submitting ? "Scheduling…" : "Schedule 1:1"}
        </Button>
      </div>
    </Modal>
  );
}
